import type { LaunchEmailSnapshot } from './launchConfigSnapshot';
import type {
    CycleCommunicationMilestoneTypeValue,
    CycleCommunicationRecipientTypeValue,
} from '@/constants/cycle-evaluation/CycleCommunication';
import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleCommunicationBlock } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { MultilingualLabel } from '@/types/Locale';

import {
    CycleCommunicationMilestoneType,
    CycleCommunicationRecipientType,
} from '@/constants/cycle-evaluation/CycleCommunication';
import {
    COMMUNICATION_BLOCK_ORDER,
    CommunicationBlockPersistenceRoute,
    isLaunchCommunicationBlock,
} from '@/pages/CycleEvaluation/Communications/communicationBlocks';
import { resolveLegacyScheduleEmailEnabled } from '@/pages/CycleEvaluation/Communications/communicationsLegacyManagerEmailPersistence';
import { CommunicationEmailAudience } from '@/pages/CycleEvaluation/Communications/components/CommunicationEmailCard/CommunicationEmailCard';

type CommunicationBlockDefinition = (typeof COMMUNICATION_BLOCK_ORDER)[number];

const communicationBlockByStepId = new Map<string, CommunicationBlockDefinition>(
    COMMUNICATION_BLOCK_ORDER.map((block) => [block.id, block]),
);

const toLocaleMap = (
    value: string | MultilingualLabel | null | undefined,
): Record<string, string> => {
    if (!value) {
        return {};
    }

    return typeof value === 'string' ? { und: value } : { ...value };
};

const toOptionalIso = (value: string | null | undefined) => value ?? undefined;

const isRecipient = ({
    recipient,
    expected,
}: {
    recipient: CycleCommunicationRecipientTypeValue;
    expected: CycleCommunicationRecipientTypeValue;
}) => recipient === expected;

/** The three launch steps share one communication block; the orphans each carry their own. */
const communicationTypeForStep = (
    definition: CommunicationBlockDefinition,
): CycleCommunicationMilestoneTypeValue =>
    isLaunchCommunicationBlock(definition)
        ? CycleCommunicationMilestoneType.MANAGER_ASSESSMENT_LAUNCH
        : definition.milestoneType;

const toBlockEmail = ({
    block,
    recipient,
}: {
    block: CycleCommunicationBlock | undefined;
    recipient: CycleCommunicationRecipientTypeValue;
}): LaunchEmailSnapshot | undefined => {
    const email = block?.emails.find((candidate) =>
        isRecipient({ recipient: candidate.recipient, expected: recipient }),
    );

    if (!block || !email) {
        return undefined;
    }

    return {
        isEnabled: block.isEnabled && email.isEnabled,
        titleByLocale: { ...email.title },
        bodyByLocale: { ...email.body },
    };
};

export interface StepEmails {
    employeeEmail?: LaunchEmailSnapshot;
    managerEmail?: LaunchEmailSnapshot;
}

/**
 * The single reader of what a step will actually send. The two launch steps kept on the legacy
 * schedule and the five on the communications contract answer the same shape here, so no consumer
 * has to know which route a step persists through.
 */
const readStepEmails = ({
    definition,
    cycleEvaluation,
    block,
}: {
    definition: CommunicationBlockDefinition;
    cycleEvaluation: CycleEvaluation | undefined;
    block: CycleCommunicationBlock | undefined;
}): StepEmails => {
    if (definition.persistenceRoute === CommunicationBlockPersistenceRoute.LEGACY_SCHEDULE) {
        const schedule = cycleEvaluation?.cycleSchedule;

        return {
            employeeEmail: {
                isEnabled: resolveLegacyScheduleEmailEnabled(
                    schedule,
                    CommunicationEmailAudience.EMPLOYEE,
                ),
                titleByLocale: toLocaleMap(schedule?.employeeEmailTitle),
                bodyByLocale: toLocaleMap(schedule?.employeeEmailMessage),
            },
            managerEmail: {
                isEnabled: resolveLegacyScheduleEmailEnabled(
                    schedule,
                    CommunicationEmailAudience.MANAGER,
                ),
                titleByLocale: toLocaleMap(schedule?.emailTitle),
                bodyByLocale: toLocaleMap(schedule?.emailMessage),
            },
        };
    }

    return {
        employeeEmail: definition.hasEmployeeCard
            ? toBlockEmail({ block, recipient: CycleCommunicationRecipientType.EMPLOYEE })
            : undefined,
        managerEmail: toBlockEmail({ block, recipient: CycleCommunicationRecipientType.MANAGER }),
    };
};

export type { CommunicationBlockDefinition };
export {
    communicationBlockByStepId,
    communicationTypeForStep,
    isRecipient,
    readStepEmails,
    toLocaleMap,
    toOptionalIso,
};
